import React, { useState, useEffect } from 'react';
import { Bell, BellOff, X, Smartphone } from 'lucide-react';
import { Button } from './Button';
import { 
  isPushSupported, 
  getNotificationPermission, 
  requestNotificationPermission,
  isPWAInstalled,
  setupForegroundMessageListener,
  showLocalNotification
} from '../../utils/pushNotificationService';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';

interface PushNotificationPromptProps {
  showAsButton?: boolean;
  className?: string;
}

export const PushNotificationPrompt: React.FC<PushNotificationPromptProps> = ({ 
  showAsButton = false,
  className = ''
}) => {
  const { currentUser } = useAuth();
  const { businessId } = useBusiness();
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    // Verificar soporte
    const supported = isPushSupported();
    setIsSupported(supported);
    setIsPWA(isPWAInstalled());
    
    if (supported) {
      setPermission(getNotificationPermission());
      
      // Mostrar banner si no ha decidido y es primera vez
      const hasSeenPrompt = localStorage.getItem('pushNotificationPromptSeen');
      if (!hasSeenPrompt && getNotificationPermission() === 'default') {
        // Esperar un poco antes de mostrar
        setTimeout(() => setShowBanner(true), 3000);
      }
      
      // Configurar listener para mensajes en primer plano
      setupForegroundMessageListener((payload) => {
        // Mostrar toast cuando llega una notificación en primer plano
        toast(payload.notification?.body || 'Nueva notificación', {
          icon: '🔔',
          duration: 5000,
        });
      });
    }
  }, []);

  const handleEnableNotifications = async () => {
    if (!currentUser?.id || !businessId) {
      toast.error('Debes iniciar sesión primero');
      return;
    }

    setIsLoading(true);
    try {
      const token = await requestNotificationPermission(currentUser.id, businessId);
      
      if (token) {
        setPermission('granted');
        setShowBanner(false);
        localStorage.setItem('pushNotificationPromptSeen', 'true');
        toast.success('¡Notificaciones activadas! 🔔');
        
        // Mostrar notificación de prueba
        setTimeout(() => {
          showLocalNotification('¡Notificaciones activadas!', {
            body: 'Recibirás alertas de tus reservas y clases.',
            tag: 'welcome'
          });
        }, 1000);
      } else {
        if (getNotificationPermission() === 'denied') {
          setPermission('denied');
          toast.error('Las notificaciones están bloqueadas. Actívalas en la configuración del navegador.');
        }
      }
    } catch (error) {
      console.error('Error activando notificaciones:', error);
      toast.error('Error al activar notificaciones');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pushNotificationPromptSeen', 'true');
  };

  // Si no está soportado, no mostrar nada
  if (!isSupported) {
    return null;
  }

  // Modo botón simple
  if (showAsButton) {
    return (
      <Button
        variant={permission === 'granted' ? 'ghost' : 'outline'}
        size="sm"
        onClick={handleEnableNotifications}
        disabled={permission === 'granted' || isLoading}
        className={className}
      >
        {permission === 'granted' ? (
          <>
            <Bell className="w-4 h-4 mr-2 text-green-500" />
            Notificaciones activas
          </>
        ) : permission === 'denied' ? (
          <>
            <BellOff className="w-4 h-4 mr-2 text-red-500" />
            Bloqueadas
          </>
        ) : (
          <>
            <Bell className="w-4 h-4 mr-2" />
            {isLoading ? 'Activando...' : 'Activar notificaciones'}
          </>
        )}
      </Button>
    );
  }

  // Banner flotante
  if (!showBanner || permission !== 'default') {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 animate-slide-in-right">
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl shadow-2xl p-4 text-white">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/20 rounded-xl">
            <Bell className="w-6 h-6" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1">
              ¿Activar notificaciones?
            </h3>
            <p className="text-sm text-white/90 mb-3">
              Recibe alertas de tus reservas, recordatorios de clases y novedades.
            </p>
            
            {!isPWA && (
              <div className="flex items-center gap-2 text-xs text-white/80 mb-3 bg-white/10 rounded-lg p-2">
                <Smartphone className="w-4 h-4" />
                <span>Tip: Instala la app para mejor experiencia</span>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleEnableNotifications}
                disabled={isLoading}
                className="bg-white text-amber-600 hover:bg-white/90 font-semibold"
              >
                {isLoading ? 'Activando...' : 'Activar'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="text-white hover:bg-white/20"
              >
                Ahora no
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;









